import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMessage } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import { Header } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { Send, MessageSquare, ArrowLeft } from "lucide-react";

function Avatar({ media, name, size = 44 }) {
  const url = mediaUrl(media);
  return (
    <div className="bg-ink border border-line overflow-hidden shrink-0" style={{ width: size, height: size }}>
      {url ? <img src={url} alt={name} className="w-full h-full object-cover" /> : null}
    </div>
  );
}

export default function Messages() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [mobileThread, setMobileThread] = useState(false);
  const endRef = useRef(null);

  const loadConvs = useCallback(async () => {
    const res = await api.get("/conversations");
    setConvs(res.data.conversations || []);
    return res.data.conversations || [];
  }, []);

  const openConv = useCallback(async (id) => {
    setActiveId(id);
    setMobileThread(true);
    const res = await api.get(`/conversations/${id}/messages`);
    setMessages(res.data.messages || []);
    setActive(res.data.conversation);
    setConvs((prev) => prev.map((c) => (c.id === id ? { ...c, unread: 0 } : c)));
  }, []);

  // Initial load; if ?to=<user_id>, start/open that conversation
  useEffect(() => {
    (async () => {
      const list = await loadConvs();
      const to = params.get("to");
      if (to) {
        try {
          const res = await api.post("/conversations/with", { recipient_id: to });
          await loadConvs();
          openConv(res.data.id);
        } catch (e) { toast.error(errMessage(e)); }
        params.delete("to");
        setParams(params, { replace: true });
      } else if (list.length) {
        openConv(list[0].id);
      }
    })();
    // eslint-disable-next-line
  }, []);

  // Light polling of the open thread + inbox
  useEffect(() => {
    if (!activeId) return;
    const t = setInterval(async () => {
      try {
        const res = await api.get(`/conversations/${activeId}/messages`);
        setMessages(res.data.messages || []);
        loadConvs();
      } catch {}
    }, 6000);
    return () => clearInterval(t);
  }, [activeId, loadConvs]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || !activeId) return;
    setSending(true);
    setText("");
    try {
      const res = await api.post(`/conversations/${activeId}/messages`, { text: t });
      setMessages((prev) => [...prev, res.data]);
      loadConvs();
    } catch (err) { toast.error(errMessage(err)); setText(t); }
    finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-ink">
      <Header />
      <div className="max-w-[1200px] mx-auto px-5 md:px-10 pt-24 pb-10">
        <button
          onClick={() => (window.history.length > 2 ? navigate(-1) : navigate("/directory"))}
          data-testid="messages-back-btn"
          className="flex w-fit items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-ash hover:text-gold transition-colors mb-5"
        >
          <ArrowLeft size={15} /> Back
        </button>
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">Direct messages</span>
        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter uppercase text-cream mt-2 mb-8">Your threads</h1>

        <div className="grid md:grid-cols-[340px_1fr] gap-px bg-line border border-line h-[64vh]">
          {/* Conversation list */}
          <div className={`bg-surface overflow-y-auto ${mobileThread ? "hidden md:block" : "block"}`}>
            {convs.length === 0 ? (
              <div className="p-8 text-center text-ash text-sm">
                <MessageSquare size={28} className="mx-auto mb-3 text-line" />
                No conversations yet. Find a writer in The Haus and hit Message.
              </div>
            ) : convs.map((c) => (
              <button
                key={c.id}
                onClick={() => openConv(c.id)}
                data-testid={`conv-${c.other.user_id}`}
                className={`w-full text-left flex items-center gap-3 p-4 border-b border-line hover:bg-ink transition-colors ${activeId === c.id ? "bg-ink" : ""}`}
              >
                <Avatar media={c.other.avatar} name={c.other.name} size={44} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-heading text-sm font-bold text-cream truncate">{c.other.name}</span>
                  </div>
                  <p className="text-ash text-xs truncate">{c.last_message || "Say hello…"}</p>
                </div>
                {c.unread > 0 && <span className="bg-gold text-ink font-mono text-[10px] font-bold px-1.5 py-0.5 shrink-0">{c.unread}</span>}
              </button>
            ))}
          </div>

          {/* Thread */}
          <div className={`bg-ink flex flex-col ${mobileThread ? "flex" : "hidden md:flex"}`}>
            {active ? (
              <>
                <div className="flex items-center gap-3 p-4 border-b border-line bg-surface">
                  <button onClick={() => setMobileThread(false)} className="md:hidden text-ash hover:text-gold"><ArrowLeft size={18} /></button>
                  <button
                    onClick={() => navigate(`/profile/${active.other.user_id}`)}
                    data-testid="thread-view-card-btn"
                    className="flex items-center gap-3 text-left group"
                    title={`View ${active.other.name}'s HausCard`}
                  >
                    <Avatar media={active.other.avatar} name={active.other.name} size={38} />
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="font-heading text-base font-bold text-cream group-hover:text-gold transition-colors underline-offset-4 group-hover:underline">{active.other.name}</span>
                      </div>
                      <span className="font-mono text-[10px] uppercase tracking-wide text-ash">{(active.other.roles || [])[0] || "Writer"} · View HausCard</span>
                    </div>
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-3">
                  {messages.length === 0 && <p className="text-ash text-sm text-center my-auto">This is the start of your conversation. Reference the song you loved and say hello.</p>}
                  {messages.map((m) => {
                    const mine = m.sender_id === user?.user_id;
                    return (
                      <div key={m.id} className={`max-w-[75%] px-4 py-2.5 ${mine ? "self-end bg-gold text-ink" : "self-start bg-surface text-cream border border-line"}`}>
                        <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                </div>

                <form onSubmit={send} className="flex gap-2 p-4 border-t border-line bg-surface">
                  <input
                    data-testid="message-input"
                    value={text} onChange={(e) => setText(e.target.value)}
                    placeholder="Write a message…"
                    className="flex-1 bg-ink border border-line text-cream px-4 py-3 focus:outline-none focus:border-gold placeholder:text-ash/50"
                  />
                  <button type="submit" disabled={sending || !text.trim()} data-testid="send-message-btn" className="bg-gold text-ink px-5 hover:bg-gold-hi transition-colors disabled:opacity-50 flex items-center justify-center"><Send size={18} /></button>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-ash text-sm p-8 text-center">Select a conversation to start messaging.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
